import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  COLOR_LABEL,
  COLOR_LABEL_KEYS,
  COLORS,
  GradientColors
} from "@/constants/colors";
import { DEFAULT_STEP, MAX_STEP, MIN_STEP } from "@/constants/dataset";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import type { ColorMap, EColorNames } from "@/types/dataset";
import { getColorScaleMap } from "@/utils/color";
import { clampStepValue } from "@/utils/stepValue";

import { twMerge } from "tailwind-merge";

type ShadeKeys = ReadonlyArray<string | number>;

interface ColorScaleSelectProps {
  titleClassName?: string;
  selectContentClassName?: string;
  selectTriggerClassName?: string;
  value?: ColorMap;
  onValueChange?: (value: ColorMap) => void;
  type?: "fill" | "stroke";
  stepValue?: number;
  maxStepValue?: number;
  onStepValueChange?: (value: number) => void;
  isNewUIDatasetConfigEnabled?: boolean;
  disabled?: boolean;
}

const ColorScaleSelect = ({
  titleClassName,
  selectTriggerClassName,
  selectContentClassName,
  value,
  onValueChange,
  stepValue = DEFAULT_STEP,
  maxStepValue = MAX_STEP,
  onStepValueChange,
  type = "fill",
  isNewUIDatasetConfigEnabled = false,
  disabled = false
}: ColorScaleSelectProps) => {
  const { isNewColorPaletteEnabled } = useAppFeatures();

  const filteredColor = isNewColorPaletteEnabled
    ? COLORS
    : Object.fromEntries(
        Object.entries(COLORS).filter(([colorName]) =>
          GradientColors.includes(colorName)
        )
      );

  const isFillOrStroke = type === "fill" || type === "stroke";
  const list = isFillOrStroke ? filteredColor : COLOR_LABEL;

  const shades = (colorName: EColorNames): ShadeKeys => {
    if (isFillOrStroke) {
      return getColorScaleMap(colorName, stepValue) || [];
    }
    return COLOR_LABEL_KEYS;
  };

  return (
    <div className="w-full space-y-2">
      <p className={twMerge("text-grey text-xs mb-1", titleClassName)}>
        Color Scale
      </p>

      <div className="flex gap-3">
        <Select
          value={value}
          disabled={disabled}
          onValueChange={(val) => {
            onValueChange?.(val as ColorMap);
          }}
        >
          <SelectTrigger
            data-testid="color-scale-select"
            className={twMerge(
              "rounded-xl h-auto w-full [&>span]:w-full",
              selectTriggerClassName
            )}
          >
            <SelectValue
              placeholder="Select a color scale"
              className="w-full"
            />
          </SelectTrigger>

          <SelectContent
            side="bottom"
            avoidCollisions={false}
            className={twMerge(
              "max-h-[var(--radix-select-content-available-height)]",
              selectContentClassName
            )}
          >
            <SelectGroup className="w-full">
              {Object.entries(list).map(([colorName, colorScale]) => (
                <SelectItem
                  key={colorName}
                  value={colorName}
                  data-testid={`color-scale-option-${colorName}`}
                  className="flex items-center gap-2 py-2 px-3 w-full [&>span]:w-full"
                >
                  <div className="flex h-3 w-full rounded-sm overflow-hidden border border-[#e5e7eb]">
                    {shades(colorName as EColorNames).map((shade) => (
                      <div
                        key={String(shade)}
                        className="flex-1 h-full"
                        style={{
                          backgroundColor: colorScale[shade]
                        }}
                      />
                    ))}
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        {isNewUIDatasetConfigEnabled && isFillOrStroke && (
          <input
            aria-label="Color scale steps"
            data-testid="color-scale-step-input"
            value={stepValue}
            min={MIN_STEP}
            max={maxStepValue}
            type="number"
            onClick={(e) => e.currentTarget.select()}
            onKeyDown={(e) => {
              const input = e.currentTarget;
              const currentValue = input.value;
              const key = e.key;
              const selectionEnd = input.selectionEnd ?? 0;
              // Prevent typing "0" if it would create a leading zero (e.g., "02", "05")
              if (key === "0") {
                const afterCursor = currentValue.slice(selectionEnd);
                const potentialValue = `0${afterCursor}`;
                // If it would start with "0" and have more than 1 digit, prevent it
                if (
                  potentialValue.length > 1 &&
                  potentialValue.startsWith("0")
                ) {
                  e.preventDefault();
                }
              }
            }}
            onChange={(e) => {
              // Clamp and normalize the input value, then call onStepValueChange if it's valid
              const clamped = clampStepValue(e.target.value, maxStepValue);
              if (clamped !== null) {
                onStepValueChange?.(clamped);
              }
            }}
            disabled={disabled}
            className="w-10 flex-grow-0 flex-shrink-1 text-center border border-[#e5e7eb] rounded-xl p-2 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none disabled:bg-white-smoke disabled:text-light-gray"
          />
        )}
      </div>
    </div>
  );
};

export default ColorScaleSelect;
